import React, { useEffect, useRef } from 'react';
import './MemoryDrawer.css';

// Right-side drawer that shows what a companion remembers about the user.
// Memories are short bullet strings extracted by the backend summariser.
// User can delete any individual one, or wipe all.

function MemoryDrawer({ companion, memories = [], onClose, onDelete, onClearAll }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className="md-overlay"
      onClick={(e) => { if (e.target === overlayRef.current) onClose?.(); }}
    >
      <aside className="md-drawer" role="dialog" aria-label="Companion memories">
        <header className="md-head">
          <div>
            <div className="md-title">What {companion?.name || 'they'} remember{(companion?.name || '').endsWith('s') ? '' : 's'}</div>
            <div className="md-sub">
              Facts gathered from your conversations. You're in control.
            </div>
          </div>
          <button className="md-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </header>

        <div className="md-body">
          {memories.length === 0 ? (
            <div className="md-empty">
              <svg width="44" height="44" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4" style={{ opacity: 0.45 }}>
                <path d="M9 11.5a3 3 0 0 1 6 0M9 11.5V14a3 3 0 0 0 6 0v-2.5"/>
                <path d="M12 14v4"/>
                <path d="M8 22h8"/>
                <path d="M5.5 8.5C5.5 5.46 8.46 3 12 3s6.5 2.46 6.5 5.5"/>
              </svg>
              <p><strong>Nothing remembered yet.</strong></p>
              <p className="md-empty-hint">
                {companion?.name || 'They'} will start saving short, durable facts about you after a handful of turns — things like your name, what you're working on, the city you live in, recurring topics — so the next chat picks up where you left off.
              </p>
              <ul className="md-empty-examples">
                <li>“Goes by <em>Riya</em>; founder of a fintech startup”</li>
                <li>“Prefers concise answers, no bullet lists”</li>
                <li>“Lives in Bengaluru, training for a half-marathon”</li>
              </ul>
              <p className="md-empty-foot">
                You'll see each fact here. Tap the × on any item to forget it.
              </p>
            </div>
          ) : (
            <ul className="md-list">
              {memories.map((m) => (
                <li key={m.id} className="md-item">
                  <span className="md-text">{m.text}</span>
                  <button
                    className="md-item-del"
                    onClick={() => onDelete?.(m.id)}
                    aria-label="Forget this"
                    title="Forget this"
                  >
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6 6 18M6 6l12 12"/>
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {memories.length > 0 && (
          <footer className="md-foot">
            <button className="md-clear" onClick={onClearAll}>
              Forget everything
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
}

export default MemoryDrawer;
